import authSvc from '@/services/authSvc';
import { UninterceptedHTTP } from '@/services/clinicalPortalSvc';
import { HTTP } from '@/services/baseHttpSvc';
import moment from 'moment';
import alertSvc from '@/services/alertSvc.js';

const X_SESSION_TOKEN = 'x-session-token';

const helpers = {
  applyTableRowFormatting(tableRow) {
    const formattedTableRow = { ...tableRow };

    const dateTime = moment(tableRow.startTime);

    // Date
    formattedTableRow.date = dateTime.format('MM[/]DD[/]YY');
    formattedTableRow.time = dateTime.format('LT');

    // Status - Capitalize first letter, lowercase other
    formattedTableRow.status = `${formattedTableRow.status[0].toUpperCase()}${formattedTableRow.status
      .slice(1)
      .toLowerCase()}`;

    return formattedTableRow;
  }
};

const state = {
  clinicalCoachingSessions: [],
  clinicalCoachingSessionsLoaded: false,
  clinicalCoachingConferences: [],
  clinicalCoachingConferencesLoaded: false,
  clinicalPortalToken: null
};

const getters = {
  sessionTableDataLoaded(state) {
    return (
      state.clinicalCoachingSessionsLoaded &&
      state.clinicalCoachingConferencesLoaded
    );
  },
  clinicalCoachingSessionTableData(state) {
    if (!getters.sessionTableDataLoaded || !state.clinicalCoachingSessions) {
      return [];
    }
    return state.clinicalCoachingSessions.map(session => {
      const sessionConference = state.clinicalCoachingConferences.find(
        conference => conference.id === session.conferenceId
      );
      return helpers.applyTableRowFormatting({
        ...session,
        recording: {
          recordingId: sessionConference ? sessionConference.recordingId : null,
          recordingStatus: sessionConference
            ? sessionConference.recordingStatus
            : null
        }
      });
    });
  }
};

const actions = {
  getMemberClinicalCoachingSessions({ commit, state }, options) {
    if (options.hasQueryUuid) {
      return HTTP.get(
        `/admin/clinical-coaching/member/${options.patientLivongoUuid}`
      ).then(resp => {
        commit('setClinicalCoachingSessions', resp.data);
      });
    } else {
      return UninterceptedHTTP.get(
        `/clinical-coaching/member/${options.patientLivongoUuid}`,
        {
          headers: {
            [X_SESSION_TOKEN]: state.clinicalPortalToken || authSvc.getToken()
          }
        }
      ).then(resp => {
        commit('setClinicalCoachingSessions', resp.data);
      });
    }
  },
  getMemberClinicalCoachingConferences({ commit, state }, options) {
    if (options.hasQueryUuid) {
      return HTTP.post(
        `/admin/clinical-coaching/conference/member/${options.patientLivongoUuid}`,
        { obo: '0053F00000Bsv2l' }
      ).then(resp => {
        commit('setClinicalCoachingConferences', resp.data);
      });
    } else {
      return UninterceptedHTTP.get(
        `/user/clinical/conference/member/${options.patientLivongoUuid}`,
        {
          headers: {
            [X_SESSION_TOKEN]: state.clinicalPortalToken || authSvc.getToken()
          }
        }
      ).then(resp => {
        commit('setClinicalCoachingConferences', resp.data);
      });
    }
  },
  getConferenceRecordingDownloadURL(store, recordingId) {
    // FAKED
    return new Promise(resolve => {
      resolve({
        data: {
          downloadUrl: 'https://example.com'
        }
      });
    });

    // TODO - Uncomment when ready for API hookup
    // return HTTP.get(`/user/clinical/conference/recording/${recordingId}`);
  },
  updateSessionStatus({ commit, state }, options) {
    const alertSuccess = status => {
      alertSvc.success(
        `Session ${options.sessionId} status has been updated to ${status}.`
      );
    };

    const alertError = status => {
      alertSvc.error(
        `Session ${options.sessionId} status could not be updated to ${status}.`
      );
    };

    if (options.hasQueryUuid) {
      return HTTP.put(
        `/admin/clinical-coaching/session/${options.sessionId}/${options.status}`,
        { obo: '0053F00000Bsv2l' }
      ).then(
        resp => {
          alertSuccess(resp.data.status);
        },
        () => {
          alertError(options.status);
        }
      );
    } else {
      return UninterceptedHTTP.put(
        `/clinical-coaching/session/${options.sessionId}/${options.status}`,
        {},
        {
          headers: {
            [X_SESSION_TOKEN]: state.clinicalPortalToken || authSvc.getToken()
          }
        }
      ).then(
        resp => {
          alertSuccess(resp.data.status);
        },
        () => {
          alertError(options.status);
        }
      );
    }
  }
};

const mutations = {
  setClinicalCoachingSessions(state, sessions) {
    state.clinicalCoachingSessions = sessions;
    state.clinicalCoachingSessionsLoaded = true;
  },
  setClinicalCoachingConferences(state, sessions) {
    state.clinicalCoachingConferences = sessions;
    state.clinicalCoachingConferencesLoaded = true;
  },
  setClinicalPortalToken(state, token) {
    state.clinicalPortalToken = token;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
